import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import trI18n from '@/common/utils/trI18n';
import { corporationAtom } from '@/state/atom/corporationAtom';

import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';
import { isShowMCPServerDropdown, isShowKibanaAnalysisOption } from './InputChatAiGroupButtons/handler/MCPServerDropdownHandler';
import tr from '@/common/utils/tr';
import useTranslationSubscription from '@/hooks/useTranslation';

/**
 * Renders selected chips below the AI Chat input, in selection order.
 * selectedMcpServers may contain the 分析機能 sentinel (Constant.KIBANA_MCP_ID) mixed with
 * real MCP server ids — the sentinel renders as the 分析機能 chip, real ids as MCP chips.
 */
function SelectedMcpServerChips ({
    mcpServerOptions,
    selectedMcpServers,
    setSelectedMcpServers,
    projectInuseDetail
}) {
    useTranslationSubscription();
    
    const corporationDetail = useRecoilValue(corporationAtom);

    const isShowMCPDropdown = useMemo(
        () => isShowMCPServerDropdown({
            corporationDetail,
            selectedCorporationDetail: corporationDetail,
            serverList: mcpServerOptions
        }),
        [corporationDetail, mcpServerOptions]
    );

    // 分析機能 chip only shows while preconditions still hold (PC-2~PC-4).
    // When precondition turns OFF, old tabs must stop showing the chip (spec Edge Case #1).
    const isShowKibana = useMemo(
        () => isShowKibanaAnalysisOption({ corporationDetail, projectInuseDetail }),
        [corporationDetail, projectInuseDetail]
    );

    const mcpServersById = useMemo(
        () => new Map((mcpServerOptions || []).map(server => [server.id, server])),
        [mcpServerOptions]
    );

    const removeChip = chipId =>
        setSelectedMcpServers(previousServers => previousServers.filter(id => id !== chipId));

    const chips = [...new Set(selectedMcpServers || [])]
        .map(id => {
            // 分析機能 sentinel — independent of MCP (外部連携) visibility,
            // but still gated by its own preconditions (PC-2~PC-4).
            if (id === Constant.KIBANA_MCP_ID) {
                if (!isShowKibana) return null;
                return (
                    <div key={id} className="selected-mcp-server-chip kibana-mcp-chip">
                        <span className="kibana-mcp-chip-label">
                            {trI18n('AI_CHAT_MENU_KIBANA_ANALYSIS')}
                        </span>
                        <button
                            type="button"
                            className="btn-reset-default selected-mcp-server-remove"
                            onClick={() => removeChip(id)}
                            aria-label={trI18n('AI_CHAT_MENU_KIBANA_ANALYSIS_REMOVE')}
                        >
                            ×
                        </button>
                    </div>
                );
            }

            // Real MCP server chip — only when 外部連携 dropdown is allowed and server exists
            if (!isShowMCPDropdown) return null;
            const server = mcpServersById.get(id);
            if (!server) return null;

            const serverDisplayName = server.displayName || Utils.serverNameWithPrefix(server.name, server.type, server.scope);

            return (
                <div key={server.id} className="selected-mcp-server-chip">
                    <span
                        aria-hidden="true"
                        className={`mcp-server-status-dot ${
                            server.isConnected !== false ? 'is-connected' : 'is-disconnected'
                        }`}
                    />
                    <OverflowTooltipContent
                        text={tr(serverDisplayName)}
                        placement="top"
                    />
                    <button
                        type="button"
                        className="btn-reset-default selected-mcp-server-remove"
                        onClick={() => removeChip(server.id)}
                        aria-label={`delete ${server.name}`}
                    >
                        ×
                    </button>
                </div>
            );
        })
        .filter(Boolean);

    if (Utils.isEmptyArray(chips)) return null;

    return <>{chips}</>;
}

export default SelectedMcpServerChips;
